import React, { useState } from "react";
import { Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Tab, Tabs } from '@mui/material';
import { getUserObject } from "../../../actions/profile/UserActions";
import { getAllSites, getAllSubmissions } from "../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { Add, AssessmentOutlined, AssignmentOutlined, EmailOutlined, PersonOutline, StoreOutlined } from "@mui/icons-material";
import { ContainedButton, TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import { CARD_HEADER_STYLE } from "../../../constants/Styles";
import { formatDateTimeToFancyString, timeAgo } from "../../../utilities/DateUtilities";
import StyledStatusIcon from "../../common/StyledStatusIcon";
import { SUBMISSION_STATUS, SUBMISSION_TYPES_HUMAN_MAP } from "../../../constants/Statuses";
import ViewRequestModal from "../modals/ViewRequestModal";
import AdminAddRequestModal from "../modals/AdminAddRequestModal";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";
import { StyledTextField } from "../../common/StyledTextField";
import { StyledTab, StyledTabs } from "../../common/StyledTabs";
import { getUserId } from "../../../selectors/UserSelectors";

const AdminComponent = ({ history }) => {

    const [requestInView, setRequestInView] = useState();
    const [openAddRequest, setOpenAddRequest] = useState();
    const [searchQuery, setSearchQuery] = useState();
    const [filterType, setFilterType] = useState(0);

    const submissions = getAllSubmissions();

    const handleViewRequestOnClose = () => {
        setRequestInView();
    }

    const handleAddRequestOnClose = () => {
        setOpenAddRequest(false)
    }

    const renderGreeting = () => {
        const today = new Date()
        if (today.getHours() < 6 ) return 'Evening'
        else if (today.getHours() >= 6 && today.getHours() < 12) return 'Morning'
        else if (today.getHours() >= 12 && today.getHours() < 18) return 'Afternoon'
        else if (today.getHours() >= 18) return 'Evening'
    }

    const getFirstName = () => (
        getUserObject() && `${getUserObject().user_personal.first_name}`
    )

    const createSubmissionTitle = (submission) => {
        const type = SUBMISSION_TYPES_HUMAN_MAP[submission.submission_type] || submission.submission_type
        if (submission.submission_identifiers.site) {
            return <><strong>{submission.submission_id.substring(submission.submission_id.length - 6).toUpperCase()}</strong>: {submission.submission_fields ? submission.submission_fields.title : 'Untitled'}</>
        } else if (submission.submission_identifiers.user) {
            return `${submission.submission_id.substring(submission.submission_id.length - 6).toUpperCase()}: ${submission.submission_identifiers.user.first_name} ${submission.submission_identifiers.user.last_name}`
        }
        return type;
    }

    const getSubmissions = () => {
        if (!submissions) return [];

        let subs = submissions;

        if (searchQuery) {
            subs = searchSubmissions(submissions, searchQuery);
        }

        if (filterType === 0) { // Filter all assigned to user
            const userId = getUserId();
            subs = subs.filter(s => s.submission_assigned_to === userId);
        } else if (filterType === 1) { // Unassigned only
            subs = subs.filter(s => !s.submission_assigned_to);
        }

        return sortSubmissions(subs);
    }

    const handleChange = (event, newValue) => {
        setFilterType(newValue);
    };

    const renderSubmissions = () => {
        const subs = getSubmissions();
        return (
            <StyledSection title={"Active Requests"} rightAdornment={
            <TooltipIconButton
                text={"Add New Request"}
                icon= {<Add sx={{ fontSize: '24px'}}/>}
                onClick={() => setOpenAddRequest(true)}
            />}>
                <StyledTextField
                    fullWidth
                    autoFocus
                    key="search"
                    value={searchQuery}
                    label="Search"
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <StyledTabs value={filterType} onChange={handleChange}>
                    <StyledTab label="Assigned To Me" selected={filterType === 0} />
                    <StyledTab label="Unassigned" selected={filterType === 1} />
                    <StyledTab label="All" selected={filterType === 2} />
                </StyledTabs>
                <Paper variant='outlined' sx={{ marginTop: '20px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {subs.map((submission, index) => (
                            <ListItem
                            dense
                            key={submission.submission_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== subs.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                    <StyledStatusIcon status={submission.submission_status} />
                                </ListItemAvatar>
                            <ListItemText
                                primary={createSubmissionTitle(submission)}
                                secondary={<><em>{submission.submission_identifiers.site.site_name}</em> | {timeAgo(submission.submission_received_epoch)}</>} 
                            />
                            <ListItemSecondaryAction>
                                <TooltipIconButton
                                    text={'View Details'}
                                    icon= {<AssignmentOutlined sx={{ color: Colors.primaryColor }}
                                    onClick={() => setRequestInView(submission)}
                                /> } />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        
                        {(!subs || subs.length === 0) &&
                            <p>No requests found.</p>
                        }
                    </List>
                </Paper>
            </StyledSection>
        )
    }

    const searchSubmissions = (subs, query) => {
        if (!query) return subs;

        const normalizedQuery = query.toLowerCase();

        return subs.filter((submission) => {
            const title =
            submission.submission_fields.title.toLowerCase();

            const siteName = 
            submission.submission_identifiers.site.site_name.toLowerCase();

            const subId =
            submission.submission_id.substring(submission.submission_id.length - 6).toLowerCase();


            return (
            title.includes(normalizedQuery) ||
            siteName.includes(normalizedQuery) ||
            subId.inclues(normalizedQuery)
            );
        });
    }

    const sortSubmissions = (subs) => {
        subs.sort((a, b) => {
            const awaitingStatus = SUBMISSION_STATUS.AWAITING_DETAILS;
        
            const aIsAwaiting = a.submission_status === awaitingStatus;
            const bIsAwaiting = b.submission_status === awaitingStatus;
        
            // If one is awaiting and the other is not, push the awaiting one to the bottom
            if (aIsAwaiting && !bIsAwaiting) return 1;
            if (!aIsAwaiting && bIsAwaiting) return -1;
        
            // Otherwise, sort by submission_received_epoch ascending
            return a.submission_received_epoch - b.submission_received_epoch;
        });
        return subs;
    }
      
    return (
        <StyledPage>
            <h3 style={{ margin: 0, paddingTop: '10px' }}>Team Panel</h3>
            <h5 style={{ margin: 0, paddingBottom: '10px' }}>Good {renderGreeting()}, {getFirstName()}</h5>
            <Box sx={{ marginTop: '0px' }}>
                {renderSubmissions()}
            </Box>

            <ViewRequestModal
                open={!!requestInView}
                onClose={handleViewRequestOnClose}
                request={requestInView}
            />
            <AdminAddRequestModal
                open={openAddRequest}
                onClose={handleAddRequestOnClose}
            />
        </StyledPage>
       
    )
}

export default withRouter(observer(AdminComponent));

